import { useState, useEffect, useCallback } from 'react';
import api from '../../lib/api';
import { toast } from 'sonner';
import { Card, CardContent } from '../ui/card';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { Label } from '../ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Checkbox } from '../ui/checkbox';
import { Loader2 } from 'lucide-react';
import { statusLabel, isCardClosed } from './conductUtils';

const SEVERITIES = [
  { id: 'MINOR', name: 'Minor' },
  { id: 'MAJOR', name: 'Major' },
  { id: 'GROSS', name: 'Gross' },
];

const EMPTY_FORM = {
  employee_id: '', category: '', proposed_severity: '', incident_at: '', channel_code: '', channel_other: '',
  what_happened: '', what_expected: '', impact_codes: [], impact_text: '', impact_usd: '',
  no_evidence_reason: 'No supporting file available', earlier_warnings: 'None', recommended_action: '',
  counterparty: null,
};

const EMPTY_CAP_ACTION = { text: '', target: '', check_method: '', due_review_day: '' };

function CounterpartyPicker({ value, onChange }) {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);

  const search = async (q) => {
    setQuery(q);
    if (q.length < 2) { setResults([]); return; }
    try {
      const res = await api.get('/conduct/counterparties', { params: { search: q } });
      setResults(res.data);
    } catch { setResults([]); }
  };

  return (
    <div className="space-y-1">
      <Label>Counterparty (customer/vendor)</Label>
      {value ? (
        <div className="flex items-center gap-2 text-sm border rounded-sm px-3 py-2">
          <span>{value.name} ({value.type})</span>
          <Button size="sm" variant="ghost" data-testid="counterparty-clear-btn" onClick={() => onChange(null)}>Change</Button>
        </div>
      ) : (
        <>
          <Input placeholder="Search vendor or customer name..." value={query} onChange={(e) => search(e.target.value)} data-testid="counterparty-search-input" />
          {results.length > 0 && (
            <div className="border rounded-sm divide-y max-h-36 overflow-y-auto">
              {results.map((r, i) => (
                <button key={i} type="button" className="w-full text-left px-3 py-2 text-sm hover:bg-muted/60"
                  data-testid={`counterparty-option-${i}`} onClick={() => { onChange(r); setResults([]); }}>
                  {r.name} <span className="text-muted-foreground">({r.type})</span>
                </button>
              ))}
            </div>
          )}
        </>
      )}
    </div>
  );
}

function CapDraftForm({ card, onDone }) {
  const existing = (card?.cap?.actions || []).map((a) => ({ ...a, due_review_day: String(a.due_review_day || '') }));
  const [actions, setActions] = useState(existing.length ? existing : [{ ...EMPTY_CAP_ACTION }, { ...EMPTY_CAP_ACTION }]);
  const reviewDays = card?.cap?.review_days || [];

  const update = (i, field, value) => {
    const copy = [...actions];
    copy[i] = { ...copy[i], [field]: value };
    setActions(copy);
  };

  const submit = async () => {
    const payload = actions.filter((a) => a.text.trim()).map((a) => ({ ...a, due_review_day: Number(a.due_review_day) }));
    if (payload.length < 2) { toast.error('A CAP needs at least 2 actions'); return; }
    try {
      await api.post(`/conduct/cards/${card.id}/cap`, { actions: payload });
      toast.success('CAP drafted and sent to HR for approval');
      onDone();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Failed to draft CAP'); }
  };

  return (
    <div className="space-y-2 border-t pt-3 mt-2" data-testid={`cap-draft-form-${card.id}`}>
      <p className="text-sm font-medium">Draft Corrective Action Plan</p>
      {actions.map((a, i) => (
        <div key={i} className="grid grid-cols-4 gap-2">
          <Input placeholder="Action" value={a.text} onChange={(e) => update(i, 'text', e.target.value)} data-testid={`cap-action-text-${card.id}-${i}`} />
          <Input placeholder="Target" value={a.target} onChange={(e) => update(i, 'target', e.target.value)} data-testid={`cap-action-target-${card.id}-${i}`} />
          <Input placeholder="Check method" value={a.check_method} onChange={(e) => update(i, 'check_method', e.target.value)} data-testid={`cap-action-check-${card.id}-${i}`} />
          <Select value={String(a.due_review_day || '')} onValueChange={(v) => update(i, 'due_review_day', v)}>
            <SelectTrigger data-testid={`cap-action-day-${card.id}-${i}`}><SelectValue placeholder="Due day" /></SelectTrigger>
            <SelectContent>{reviewDays.map((d) => <SelectItem key={d} value={String(d)}>{d}</SelectItem>)}</SelectContent>
          </Select>
        </div>
      ))}
      <div className="flex gap-2">
        {actions.length < 4 && <Button size="sm" variant="outline" onClick={() => setActions([...actions, { ...EMPTY_CAP_ACTION }])}>Add Action</Button>}
        <Button size="sm" data-testid={`submit-cap-draft-btn-${card.id}`} onClick={submit}>Submit CAP</Button>
      </div>
    </div>
  );
}

function MyReportRow({ c, categoryName, onChanged }) {
  const [note, setNote] = useState('');

  const sendNote = async () => {
    if (!note.trim()) { toast.error('Please write a note'); return; }
    try {
      await api.post(`/conduct/cases/${c.id}/notes`, { text: note });
      toast.success('Note sent to HR');
      setNote('');
      onChanged();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Failed to send note'); }
  };

  const cardOpen = c.card_id && !isCardClosed(c.card_status);
  const needsCapDraft = cardOpen && c.cap && !c.cap.approved_by && (!c.cap.drafted_by || c.cap.sent_back_reason);
  const awaitingApproval = cardOpen && c.cap && !c.cap.approved_by && c.cap.drafted_by && !c.cap.sent_back_reason;
  const sentBackReason = cardOpen && c.cap && !c.cap.approved_by && c.cap.sent_back_reason;

  return (
    <Card data-testid={`my-report-${c.id}`}>
      <CardContent className="pt-4 space-y-2">
        <div className="flex items-center justify-between">
          <span className="text-sm">{c.employee_name} — {categoryName(c.category)} — {c.proposed_severity}</span>
          <Badge variant="outline">{statusLabel(c.status, c, 'hr')}</Badge>
        </div>
        {c.status === 'INFO_REQUESTED' && (
          <div className="flex gap-2 items-center">
            <Textarea className="h-8 text-xs" placeholder="Reply to HR's request for more information"
              data-testid={`info-reply-input-${c.id}`} value={note} onChange={(e) => setNote(e.target.value)} />
            <Button size="sm" data-testid={`info-reply-btn-${c.id}`} onClick={sendNote}>Send</Button>
          </div>
        )}
        {sentBackReason && (
          <p className="text-xs text-amber-700" data-testid={`cap-sent-back-notice-${c.id}`}>
            HR sent the CAP back for changes: {sentBackReason}
          </p>
        )}
        {needsCapDraft && <CapDraftForm card={{ id: c.card_id, cap: c.cap }} onDone={onChanged} />}
        {awaitingApproval && (
          <p className="text-xs text-muted-foreground" data-testid={`cap-awaiting-approval-${c.id}`}>
            Corrective Action Plan submitted — awaiting HR approval.
          </p>
        )}
      </CardContent>
    </Card>
  );
}

export default function ReportIssueTab() {
  const [employees, setEmployees] = useState([]);
  const [categories, setCategories] = useState([]);
  const [myReports, setMyReports] = useState([]);
  const [channelCodes, setChannelCodes] = useState([]);
  const [impactCodes, setImpactCodes] = useState([]);
  const [form, setForm] = useState(EMPTY_FORM);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [empRes, catRes, reportsRes, optRes] = await Promise.all([
        api.get('/conduct/team/reportable-employees'),
        api.get('/conduct/categories'),
        api.get('/conduct/cases/mine'),
        api.get('/conduct/report-form-options'),
      ]);
      setEmployees(empRes.data);
      setCategories(catRes.data);
      setMyReports(reportsRes.data);
      setChannelCodes(optRes.data.channel_codes);
      setImpactCodes(optRes.data.impact_codes);
    } catch (e) {
      toast.error('Failed to load reporting form');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const categoryName = (id) => categories.find((c) => c.id === id)?.name || id;
  const set = (field, value) => setForm({ ...form, [field]: value });
  const toggleImpact = (code) => {
    const has = form.impact_codes.includes(code);
    const next = has ? form.impact_codes.filter((c) => c !== code) : [...form.impact_codes, code];
    setForm({ ...form, impact_codes: next, impact_usd: moneyOrDeal(next) ? form.impact_usd : '' });
  };
  const moneyOrDeal = (codes) => codes.includes('MONEY') || codes.includes('DEAL');
  const onlyNone = form.impact_codes.length === 1 && form.impact_codes[0] === 'NONE';

  const submit = async () => {
    if (!form.employee_id || !form.category || !form.proposed_severity || !form.incident_at ||
        !form.channel_code || !form.what_happened || !form.what_expected || form.impact_codes.length === 0) {
      toast.error('Please fill in all required fields');
      return;
    }
    if (form.channel_code === 'OTHER' && !form.channel_other.trim()) {
      toast.error('Please describe the channel');
      return;
    }
    if (!onlyNone && !form.impact_text.trim()) {
      toast.error('Please describe the impact');
      return;
    }
    if (form.category === 'customer_vendor' && !form.counterparty) {
      toast.error('Please select the counterparty involved');
      return;
    }
    setSubmitting(true);
    try {
      const payload = { ...form, impact_usd: moneyOrDeal(form.impact_codes) && form.impact_usd ? Number(form.impact_usd) : null, declaration_accepted: true };
      await api.post('/conduct/cases', payload);
      toast.success('Report submitted to HR');
      setForm(EMPTY_FORM);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to submit report');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) return <div className="flex justify-center py-12" data-testid="report-issue-loading"><Loader2 className="animate-spin" /></div>;

  return (
    <div className="space-y-6" data-testid="report-issue-tab">
      <Card data-testid="report-issue-form">
        <CardContent className="pt-6 space-y-4">
          <h3 className="font-semibold">Report a Conduct Issue</h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <Label>Employee</Label>
              <Select value={form.employee_id} onValueChange={(v) => set('employee_id', v)}>
                <SelectTrigger data-testid="report-employee-select"><SelectValue placeholder="Select employee" /></SelectTrigger>
                <SelectContent>
                  {employees.map((e) => <SelectItem key={e.id} value={e.id}>{e.staff_number ? `${e.name} (${e.staff_number})` : e.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Category</Label>
              <Select value={form.category} onValueChange={(v) => set('category', v)}>
                <SelectTrigger data-testid="report-category-select"><SelectValue placeholder="Select category" /></SelectTrigger>
                <SelectContent>
                  {categories.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Severity</Label>
              <Select value={form.proposed_severity} onValueChange={(v) => set('proposed_severity', v)}>
                <SelectTrigger data-testid="report-severity-select"><SelectValue placeholder="Select severity" /></SelectTrigger>
                <SelectContent>
                  {SEVERITIES.map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>When did it happen?</Label>
              <Input data-testid="report-incident-at" type="datetime-local" value={form.incident_at} onChange={(e) => set('incident_at', e.target.value)} />
            </div>
            <div>
              <Label>Channel</Label>
              <Select value={form.channel_code} onValueChange={(v) => set('channel_code', v)}>
                <SelectTrigger data-testid="report-channel-select"><SelectValue placeholder="How did it happen?" /></SelectTrigger>
                <SelectContent>
                  {channelCodes.map((c) => <SelectItem key={c.code} value={c.code}>{c.label}</SelectItem>)}
                </SelectContent>
              </Select>
              {form.channel_code === 'OTHER' && (
                <Input className="mt-2" data-testid="report-channel-other" placeholder="Describe the channel"
                  value={form.channel_other} onChange={(e) => set('channel_other', e.target.value)} />
              )}
            </div>
          </div>
          {form.category === 'customer_vendor' && (
            <CounterpartyPicker value={form.counterparty} onChange={(v) => set('counterparty', v)} />
          )}
          <div>
            <Label>What happened?</Label>
            <Textarea data-testid="report-what-happened" value={form.what_happened} onChange={(e) => set('what_happened', e.target.value)} />
          </div>
          <div>
            <Label>What was expected instead?</Label>
            <Textarea data-testid="report-what-expected" value={form.what_expected} onChange={(e) => set('what_expected', e.target.value)} />
          </div>
          <div className="space-y-2">
            <Label>Impact (tick at least one)</Label>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              {impactCodes.map((c) => (
                <div key={c.code} className="flex items-center gap-2">
                  <Checkbox id={`impact-${c.code}`} data-testid={`report-impact-${c.code}`}
                    checked={form.impact_codes.includes(c.code)} onCheckedChange={() => toggleImpact(c.code)} />
                  <Label htmlFor={`impact-${c.code}`} className="text-sm font-normal">{c.label}</Label>
                </div>
              ))}
            </div>
          </div>
          {moneyOrDeal(form.impact_codes) && (
            <div>
              <Label>Impact (USD) — optional</Label>
              <Input data-testid="report-impact-usd" type="number" value={form.impact_usd} onChange={(e) => set('impact_usd', e.target.value)} />
            </div>
          )}
          {!onlyNone && (
            <div>
              <Label>Impact details</Label>
              <Textarea data-testid="report-impact-text" value={form.impact_text} onChange={(e) => set('impact_text', e.target.value)} />
            </div>
          )}
          <div>
            <Label>Earlier warnings (enter "None" if there are none)</Label>
            <Input data-testid="report-earlier-warnings" value={form.earlier_warnings} onChange={(e) => set('earlier_warnings', e.target.value)} />
          </div>
          <Button data-testid="submit-report-btn" onClick={submit} disabled={submitting}>
            {submitting ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}Submit Report
          </Button>
        </CardContent>
      </Card>

      <div>
        <h3 className="font-semibold mb-2">My Reports</h3>
        {myReports.length === 0 && <p className="text-sm text-muted-foreground" data-testid="no-my-reports-msg">You haven't filed any reports.</p>}
        <div className="space-y-2">
          {myReports.map((c) => <MyReportRow key={c.id} c={c} categoryName={categoryName} onChanged={load} />)}
        </div>
      </div>
    </div>
  );
}
